import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from './UI';

const CATEGORY_EMOJI = {
  cellphones: '📱', tablets: '📋', laptops: '💻', servers: '🖥️',
  hard_drives: '💿', ddr_ram: '🧩', other: '📦',
};

export default function ListingCard({ listing, hidePricing = false }) {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  const endsAt = new Date(listing.auctionEndsAt);
  const now = new Date();
  const msLeft = endsAt - now;
  const hoursLeft = Math.floor(msLeft / (1000 * 60 * 60));
  const minutesLeft = Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60));
  const timeStr = msLeft <= 0 ? 'Ended' : hoursLeft > 0 ? `${hoursLeft}h ${minutesLeft}m` : `${minutesLeft}m`;
  const isUrgent = msLeft > 0 && msLeft < 60 * 60 * 1000; // less than 1 hour

  const imageUrl = listing.files?.[0]?.publicUrl;
  const emoji = CATEGORY_EMOJI[listing.category] || '📦';

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => navigate(`/listing/${listing.id}`)}
      style={{
        background: 'var(--surface)', border: `1px solid ${hovered ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-lg)', overflow: 'hidden', transition: 'all 0.3s',
        transform: hovered ? 'translateY(-4px)' : 'none',
        boxShadow: hovered ? '0 12px 40px rgba(34,214,122,0.08)' : 'none', cursor: 'pointer',
      }}
    >
      {/* Image */}
      <div style={{
        height: 160, background: 'var(--bg2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 56, position: 'relative', borderBottom: '1px solid var(--border)',
        backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
        backgroundSize: 'cover', backgroundPosition: 'center',
      }}>
        {!imageUrl && emoji}
        <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 6 }}>
          {msLeft > 0 ? (
            <Badge color="green" small>
              <span className="pulse-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', display: 'inline-block' }} />
              LIVE
            </Badge>
          ) : (
            <Badge color="neutral" small>ENDED</Badge>
          )}
          {isUrgent && <Badge color="red" small>ENDING SOON</Badge>}
        </div>
        <div style={{ position: 'absolute', top: 12, right: 12 }}>
          <Badge color="neutral" small>{listing.category?.replace('_', ' ')}</Badge>
        </div>
      </div>

      <div style={{ padding: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--sans)', marginBottom: 6, lineHeight: 1.3 }}>
          {listing.title}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {listing.specifications || listing.description}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
          <Badge color="blue" small>{listing.condition}</Badge>
          {listing.grade && <Badge color="neutral" small>Grade {listing.grade}</Badge>}
          {listing.quantity && <Badge color="neutral" small>{listing.quantity} {listing.quantityUnit === 'lbs' ? 'lbs' : 'pcs'}</Badge>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div style={{ fontSize: 13, color: msLeft <= 0 ? 'var(--text3)' : isUrgent ? 'var(--red)' : 'var(--amber)', fontFamily: 'var(--mono)', fontWeight: 500 }}>
            {msLeft > 0 ? `⏱ ${timeStr} remaining` : '✓ Auction Closed'}
          </div>
        </div>
      </div>
    </div>
  );
}
